import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {BehaviorSubject, Observable} from 'rxjs';
import {switchMap, tap} from 'rxjs/operators';
import {Globals} from '../../global/globals';
import {CurrentUserService} from '../user/current-user.service';
import {UserNotificationPage, UserSettings} from '../../dtos/notification/user-notification';

/**
 * in-app notifications (Benachrichtigungen) and the user settings, not to be confused with
 * {@link NotificationService}, which shows snack bars
 */
@Injectable({
  providedIn: 'root'
})
export class UserNotificationService {

  /** last known number of unread notifications, e.g. for the badge in the header */
  readonly unreadCount$ = new BehaviorSubject<number>(0);

  private usersBaseUri: string = this.globals.backendUri + '/users/';

  constructor(private httpClient: HttpClient, private globals: Globals, private currentUserService: CurrentUserService) {
  }

  getNotifications(unreadOnly: boolean, page: number, size: number): Observable<UserNotificationPage> {
    const params = new HttpParams()
      .set('unreadOnly', String(unreadOnly))
      .set('page', String(page))
      .set('size', String(size));
    return this.withUri('/notifications', uri => this.httpClient.get<UserNotificationPage>(uri, {params})).pipe(
      tap(result => this.unreadCount$.next(result.unreadCount))
    );
  }

  refreshUnreadCount(): Observable<{ count: number }> {
    return this.withUri('/notifications/unread-count', uri => this.httpClient.get<{ count: number }>(uri)).pipe(
      tap(result => this.unreadCount$.next(result.count))
    );
  }

  markRead(id: number): Observable<void> {
    return this.withUri('/notifications/' + id + '/read', uri => this.httpClient.put<void>(uri, null));
  }

  markAllRead(): Observable<void> {
    return this.withUri('/notifications/read-all', uri => this.httpClient.put<void>(uri, null)).pipe(
      tap(() => this.unreadCount$.next(0))
    );
  }

  delete(id: number): Observable<void> {
    return this.withUri('/notifications/' + id, uri => this.httpClient.delete<void>(uri));
  }

  getSettings(): Observable<UserSettings> {
    return this.withUri('/settings', uri => this.httpClient.get<UserSettings>(uri));
  }

  updateSettings(settings: UserSettings): Observable<UserSettings> {
    return this.withUri('/settings', uri => this.httpClient.put<UserSettings>(uri, settings));
  }

  private withUri<T>(path: string, request: (uri: string) => Observable<T>): Observable<T> {
    return this.currentUserService.getUserId().pipe(
      switchMap(userId => request(this.usersBaseUri + userId + path))
    );
  }
}
